import { useEffect } from 'react';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Link, RouteComponentProps } from '@reach/router';
import { useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { DisplayInlineError } from '../../common/ErrorHandling';
import LoadingScreen from '../../ManagementPanel/components/LoadingScreen';
import { TableRouterLink } from './AdminPolicyStreamPlansTable';
import { graphql } from '../../../gql';
import { formatDateTimeForDisplay } from '../../../util';

const QUOTE_DETAILS_PATH = '/insurtech/manager/quotes/details';
const COMPULIFE_CALLS_BASE = '/insurtech/admin/policystream/compulifecalls';

interface PolicyStreamPlanDetailAdminPageProps
  extends RouteComponentProps<{ planId: string }> {}

export const PolicyStreamPlanDetailAdminPage = ({
  planId,
}: PolicyStreamPlanDetailAdminPageProps) => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error }] = useLazyQuery(
    ADMIN_POLICYSTREAM_PLAN_BY_ID,
  );

  useEffect(() => {
    if (token && planId) {
      runQuery({ variables: { token, id: planId } });
    }
  }, [token, planId, runQuery]);

  const plan = data?.policystreamPlanById;

  if (!planId) {
    return (
      <PageSection>
        <DisplayInlineError error={new Error('Missing plan id.')} />
      </PageSection>
    );
  }

  if (!token) {
    return (
      <PageSection>
        <Typography>Sign in to view this plan.</Typography>
      </PageSection>
    );
  }

  return (
    <PageSection>
      <Typography variant='h5' component='h1' gutterBottom>
        PolicyStream plan {plan?.id ?? planId}
      </Typography>
      <BackLink to='/insurtech/admin/policystream/plans'>
        &larr; All plans
      </BackLink>

      <DisplayInlineError error={error} />

      {loading && !data ? <LoadingScreen /> : null}

      {!loading && data && !plan ? (
        <Typography>Plan not found.</Typography>
      ) : null}

      {plan ? (
        <DetailGrid>
          <DetailTerm>id</DetailTerm>
          <DetailDef>{plan.id}</DetailDef>

          <DetailTerm>createdAt</DetailTerm>
          <DetailDef>{formatDateTimeForDisplay(plan.createdAt)}</DetailDef>

          <DetailTerm>coverageAmountDollars</DetailTerm>
          <DetailDef>{plan.coverageAmountDollars ?? '—'}</DetailDef>

          <DetailTerm>premiumMonthlyDollars</DetailTerm>
          <DetailDef>{plan.premiumMonthlyDollars}</DetailDef>

          <DetailTerm>premiumQuarterlyDollars</DetailTerm>
          <DetailDef>{plan.premiumQuarterlyDollars}</DetailDef>

          <DetailTerm>premiumAnnualDollars</DetailTerm>
          <DetailDef>{plan.premiumAnnualDollars}</DetailDef>

          <DetailTerm>termLength</DetailTerm>
          <DetailDef>
            {plan.termLength !== null && plan.termLength !== undefined
              ? String(plan.termLength)
              : '—'}
          </DetailDef>

          <DetailTerm>termLengthDescription</DetailTerm>
          <DetailDef>{plan.termLengthDescription ?? '—'}</DetailDef>

          <DetailTerm>insurer</DetailTerm>
          <DetailDef>
            {plan.insurer
              ? `${plan.insurer.displayName} (${plan.insurer.id})`
              : '—'}
          </DetailDef>

          <DetailTerm>quote</DetailTerm>
          <DetailDef>
            {plan.quote?.uniqueID ? (
              <TableRouterLink
                to={`${QUOTE_DETAILS_PATH}/${String(plan.quote.uniqueID)}`}
              >
                {String(plan.quote.uniqueID)}
              </TableRouterLink>
            ) : (
              '—'
            )}
          </DetailDef>

          <DetailTerm>archivedQuote</DetailTerm>
          <DetailDef>
            {plan.archivedQuote?.uniqueID ? (
              <TableRouterLink
                to={`${QUOTE_DETAILS_PATH}/${String(
                  plan.archivedQuote.uniqueID,
                )}`}
              >
                {String(plan.archivedQuote.uniqueID)}
              </TableRouterLink>
            ) : (
              '—'
            )}
          </DetailDef>

          <DetailTerm>compulifeApiCall</DetailTerm>
          <DetailDef>
            {plan.compulifeApiCall?.id ? (
              <TableRouterLink
                to={`${COMPULIFE_CALLS_BASE}/${plan.compulifeApiCall.id}`}
              >
                {plan.compulifeApiCall.id}
              </TableRouterLink>
            ) : (
              '—'
            )}
          </DetailDef>

          <DetailTerm>markdownDetails</DetailTerm>
          <MarkdownBlock>{plan.markdownDetails}</MarkdownBlock>
        </DetailGrid>
      ) : null}
    </PageSection>
  );
};

const PageSection = styled('section')(({ theme }) => {
  return { paddingTop: theme.spacing(2) };
});

const BackLink = styled(Link)(({ theme }) => {
  return {
    display: 'inline-block',
    marginBottom: theme.spacing(2),
  };
});

const DetailGrid = styled('dl')(({ theme }) => {
  return {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: theme.spacing(1, 3),
    alignItems: 'start',
    maxWidth: 960,
  };
});

const DetailTerm = styled('dt')(({ theme }) => {
  return {
    margin: 0,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  };
});

const DetailDef = styled('dd')(() => {
  return { margin: 0 };
});

const MarkdownBlock = styled('dd')(({ theme }) => {
  return {
    margin: 0,
    gridColumn: '1 / -1',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
    fontSize: theme.typography.body2.fontSize,
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  };
});

const ADMIN_POLICYSTREAM_PLAN_BY_ID = graphql(`
  query AdminPolicystreamPlanById($token: String!, $id: ID!) {
    policystreamPlanById(token: $token, id: $id) {
      ...AdminPolicyStreamPlanTableFields
    }
  }
`);
